import { authFetch, isServerProxyDocUrl } from "../../../utils/auth";

/**
 * Split an absolute-or-relative document URL into a same-origin endpoint and
 * its fragment. The backend emits absolute URLs (`${BASE_URL}/api/documents/…`),
 * so the host is discarded and the request is issued same-origin with the
 * caller's bearer token attached by authFetch.
 */
const toEndpoint = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return { path: parsed.pathname.replace(/['"]+$/, "") + parsed.search, hash: parsed.hash || "" };
  } catch {
    const h = url.indexOf("#");
    return h >= 0 ? { path: url.slice(0, h), hash: url.slice(h) } : { path: url, hash: "" };
  }
};

const LOADING_HTML = '<p style="font:14px system-ui,sans-serif;padding:24px;color:#6e7687">Loading document…</p>';

/**
 * Open a document referenced from a copilot answer.
 *
 * External links open directly. Auth-gated proxy documents
 * (`/api/documents/…`, `/api/images/…`) are fetched with the user's bearer
 * token and handed to the new tab as a blob.
 *
 * On failure the placeholder tab is closed and the reason is reported through
 * `onError` so it surfaces in the dashboard rather than as a blank tab. Auth
 * expiry is reported but never tears down the session — see notifyOnExpiry.
 *
 * @param {string} url document URL from the answer or source card
 * @param {(message: string) => void} [onError] receives a user-facing reason
 */
export async function openDocument(url, onError) {
  if (!url) return;

  if (!isServerProxyDocUrl(url)) {
    window.open(url, "_blank", "noopener");
    return;
  }

  // Opened synchronously inside the click gesture to dodge popup blockers.
  const win = window.open("", "_blank");
  if (win) win.document.write(LOADING_HTML);

  try {
    const { path, hash } = toEndpoint(url);
    const response = await authFetch(path, {}, { notifyOnExpiry: false });
    if (!response.ok) {
      throw new Error(
        response.status === 401
          ? "Your session has expired. Sign in again to open this document."
          : `Could not open document (${response.status}).`
      );
    }

    const objectUrl = URL.createObjectURL(await response.blob());
    if (win) win.location.href = objectUrl + hash;
    else window.open(objectUrl + hash, "_blank");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    if (win && !win.closed) win.close();
    onError?.(err?.message || "Could not open document.");
  }
}
